import { PropertyValues, TemplateResult, html } from 'lit';
import { property, customElement, query, state } from 'lit/decorators.js';
import { styles } from '../view/styles';
import { FormFieldInput } from '../../gzForm/formField/component/FormFieldInput';
import { PhoneInputStrings, PhoneNumber } from '../types';
import { InputWidthValues } from '../../gzInput';
import { GzSingleSelectPhoneCodeWebComponent } from '../../gzSingleSelectPhoneCode';
import { phoneCodes } from '../utils/phone-codes';
import { isEmpty, toNumber } from 'lodash';
import { WEB_COMPONENT_PHONE_INPUT } from '../../../webcomponents.constants';
import { FontSizes } from '../../../types/common/types';

@customElement(WEB_COMPONENT_PHONE_INPUT)
export class GzPhoneInputWebComponent extends FormFieldInput<PhoneNumber> {
  @property({ type: String, attribute: true, reflect: true })
  inputWidth: InputWidthValues = InputWidthValues.MEDIUM;

  @property({ type: Boolean, attribute: true, reflect: true })
  open = false;

  @property({ type: Boolean, attribute: true, reflect: true })
  required = false;

  @property({ type: Boolean, attribute: true, reflect: true })
  showValidIcon = false;

  @property({ type: String, attribute: true, reflect: true })
  countryCode = '';

  @property({ type: String, attribute: true, reflect: true })
  subscriberNumber = '';

  @property({ type: Object, attribute: true, reflect: true })
  strings: PhoneInputStrings;

  @query('gz-single-select-phone-code')
  codePhoneSelect!: GzSingleSelectPhoneCodeWebComponent;

  @query('gz-input')
  inputElement!: HTMLInputElement;

  @query('#number-form-field')
  numberFormField!: FormFieldInput<string>;

  @state() disabledNumberInput = true;

  static get styles() {
    return styles;
  }

  public setFormValue(value: PhoneNumber): void {
    const phoneCodeValue = phoneCodes.find(code => code.countryCode === value.countryCode)?.value ?? '';
    this.countryCode = value.countryCode;
    this.codePhoneSelect?.setFormValue(phoneCodeValue);
    this.subscriberNumber = value.subscriberNumber;
  }

  public getFormValue(): PhoneNumber {
    return this.getPhoneValue();
  }

  public areFormValuesEqual(value1: PhoneNumber, value2: PhoneNumber) {
    return value1.countryCode === value2.countryCode && value1.subscriberNumber === value2.subscriberNumber;
  }

  public getPhoneValue(): PhoneNumber {
    return { countryCode: this.countryCode, subscriberNumber: this.subscriberNumber } as PhoneNumber;
  }

  render(): TemplateResult {
    return html`<div class="component-wrapper">${this.renderContent()}</div>`;
  }

  private renderContent(): TemplateResult {
    return html`${this.renderLabel()} ${this.renderPhoneInput()}`;
  }

  updated(changedProperties: PropertyValues): void {
    this.updateErrorState();
    if (changedProperties.has('countryCode') || changedProperties.has('subscriberNumber')) {
      this.setFormValue(this.getPhoneValue());
    }
    if (changedProperties.has('inputWidth')) {
      this.inputElement.style.width = this.returnInputWidth();
    }
  }

  protected firstUpdated(_changedProperties: PropertyValues): void {
    const isCountryCodeValid =
      !isEmpty(this.countryCode) && phoneCodes.some(code => code.countryCode === this.countryCode);

    if (isCountryCodeValid || this.readonly) {
      this.disabledNumberInput = false;
      this.codePhoneSelect?.setFormValue(this.countryCode);
    }
  }

  private returnInputWidth(): string {
    if (this.inputWidth === 'full') {
      return 'calc(100% - 1px)';
    } else {
      const width = toNumber(this.inputWidth);
      return `${width - 1}px`;
    }
  }

  private renderLabel(): TemplateResult {
    return isEmpty(this.strings?.label)
      ? html``
      : html`<gz-text fontsize="${FontSizes.PARAGRAPH_12}" class="label"
          >${this.strings?.label}${this.renderRequiredSymbol()}:</gz-text
        >`;
  }

  private renderRequiredSymbol(): TemplateResult {
    return this.required ? html`<span class="required-symbol">*</span>` : html``;
  }

  private renderPhoneInput(): TemplateResult {
    return html`<div>
      <div class="phone-input-wrapper">
        ${this.renderDropdown()}
        <span class="divider"></span>
        <gz-input
          id="number-form-field"
          gz-form-input
          label=""
          placeholder="${this.getPlaceholder()}"
          inputwidth="${this.inputWidth}"
          .disabled="${this.disabledNumberInput || this.readonly}"
          @change="${this.handleInputChange}"
          .value="${this.subscriberNumber ?? ''}"
          .showValidIcon="${this.showValidIcon && this.subscriberNumber && !this.invalid}"
        ></gz-input>
      </div>
    </div>`;
  }

  private renderDropdown(): TemplateResult {
    return html`<gz-single-select-phone-code
      .filterOptions="${phoneCodes}"
      ?readonly="${this.readonly}"
      .searchPlaceholderString=${this.strings?.searchPlaceholderString}
      .noSearchResultsString=${this.strings?.noSearchResultsString}
      labelWidth="${InputWidthValues.SMALL}"
      label="${this.strings?.phoneCodeLabel}"
      @selectionChange="${this.handleOptionChange}"
      @open="${() => this.handleDropdownAction(true)}"
      @close="${() => this.handleDropdownAction(false)}"
    ></gz-single-select-phone-code>`;
  }

  private getPlaceholder(): string {
    return !this.disabledNumberInput
      ? this.strings?.numberPlaceholder ?? ''
      : this.strings?.emptyCodeValuePlaceholder ?? '';
  }

  private handleDropdownAction(isOpen: boolean): void {
    this.open = isOpen;

    if (!this.open) {
      phoneCodes.forEach(code => {
        code.hidden = false;
      });
    }
  }

  private handleOptionChange(): void {
    const selectedOption = this.codePhoneSelect?.getFormValue();
    if (selectedOption) {
      this.disabledNumberInput = false;
      this.countryCode = phoneCodes.find(code => code.value === selectedOption)?.countryCode ?? '';
    }
    this.emitChangeEvent();
  }

  private handleInputChange(event: CustomEvent): void {
    const inputValue = event.detail.sourceEvent;
    this.subscriberNumber = inputValue;
    this.emitChangeEvent();
  }

  private emitChangeEvent(): void {
    const event = new CustomEvent('change', {
      detail: {
        countryCode: this.countryCode,
        subscriberNumber: this.subscriberNumber,
      },
      bubbles: true,
      cancelable: true,
      composed: true,
    });

    this.dispatchEvent(event);
  }

  private updateErrorState(): void {
    if (this.invalid) {
      this.numberFormField?.markAsInvalid();
    } else {
      this.numberFormField?.markAsValid();
    }
  }
}

declare global {
  interface HTMLElementTagNameMap {
    WEB_COMPONENT_PHONE_INPUT: GzPhoneInputWebComponent;
  }
}
